import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useChatStore } from "@/hooks/useChatStore";
import { useSessionPrewarm } from "./useSessionPrewarm";

interface LoadedMessage {
  id: string;
  feedbackRating?: string | null;
}

interface SessionRef {
  id: string;
  threadId: string;
  messageCount: number;
}

export interface UseChatSessionsParams {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
  /** Called with a freshly loaded page of messages, for feedback hydration. */
  onMessagesLoaded: (messages: LoadedMessage[]) => void;
  /** Called when a rollback left no messages to hydrate from. */
  onMessagesReset: () => void;
}

/**
 * Session lifecycle for the chat panel: load the building's sessions, keep the
 * active one's thread hydrated, prewarm its agent client, and expose the
 * session-list callbacks the header needs.
 *
 * Kept out of `index.tsx` because it is a self-contained state machine —
 * sessions load, the active session changes, each transition prewarms and
 * hydrates — that the container only consumes the result of.
 */
export function useChatSessions({
  buildingId,
  orgId,
  userIdentifier,
  onMessagesLoaded,
  onMessagesReset,
}: UseChatSessionsParams) {
  const { runSessionPrewarm, ...sessionState } = useActiveSessionState({
    buildingId,
    orgId,
    userIdentifier,
  });
  const { activeSession, sessionsLoading, threadId } = sessionState;

  const { loadMessagesFor, activateSession } = useSessionActivation({
    buildingId,
    orgId,
    userIdentifier,
    onMessagesLoaded,
    runSessionPrewarm,
  });

  useSessionBootstrap({ buildingId, orgId, userIdentifier, sessionsLoading, activeSession, activateSession }); // prettier-ignore

  const { animatingTitle, setAnimatingTitle } = useSessionTitleAnimation();

  const sessionActions = useSessionActions({
    buildingId,
    orgId,
    userIdentifier,
    loadMessagesFor,
    runSessionPrewarm,
  });

  const refresh = useChatRefresh({
    buildingId,
    orgId,
    threadId,
    activeSession,
    onMessagesLoaded,
    onMessagesReset,
  });

  return { ...sessionState, animatingTitle, setAnimatingTitle, ...sessionActions, ...refresh };
}

/** What the panel reads straight off the store for the building's active session. */
function useActiveSessionState({
  buildingId,
  orgId,
  userIdentifier,
}: {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
}) {
  const chatStore = useChatStore();
  const { blockingPrewarmSessionId, runSessionPrewarm } = useSessionPrewarm(orgId ?? null);

  const activeSession = chatStore.getActiveSession(buildingId);
  const sessionsLoading = chatStore.sessionsLoading;
  const activeThread = activeSession?.threadId
    ? chatStore.threads.get(activeSession.threadId)
    : null;

  // Use active session's threadId if available, otherwise fall back to legacy format
  const threadId = useMemo(() => {
    if (!orgId || !userIdentifier) return null;
    if (activeSession?.threadId) return activeSession.threadId;
    return `${orgId}-${userIdentifier}-${buildingId}`;
  }, [buildingId, orgId, userIdentifier, activeSession?.threadId]);

  return {
    runSessionPrewarm,
    sessions: chatStore.getSessionsForBuilding(buildingId),
    activeSession,
    activeSessionId: chatStore.getActiveSessionId(buildingId),
    sessionsLoading,
    threadId,
    isSessionBootstrapPending:
      sessionsLoading ||
      (Boolean(activeSession?.id) && activeSession.messageCount > 0 && !activeThread?.isHydrated) ||
      (Boolean(activeSession?.id) && blockingPrewarmSessionId === activeSession?.id),
  };
}

/** Hydrating a session's messages, and the prewarm-then-hydrate activation. */
function useSessionActivation({
  buildingId,
  orgId,
  userIdentifier,
  onMessagesLoaded,
  runSessionPrewarm,
}: {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
  onMessagesLoaded: (messages: LoadedMessage[]) => void;
  runSessionPrewarm: (session: { id: string; messageCount: number }) => Promise<unknown>;
}) {
  const chatStore = useChatStore();

  /** Load a session's messages and hand them to the feedback hydrator. */
  const loadMessagesFor = useCallback(
    (session: { id: string; threadId: string }) => {
      if (!orgId || !userIdentifier) return;
      chatStore
        .loadSessionMessages({
          sessionId: session.id,
          threadId: session.threadId,
          buildingId,
          orgId,
          userId: userIdentifier,
        })
        .then((result: { messages?: LoadedMessage[] } | undefined) => {
          // `onMessagesLoaded` replaces the whole rating map, so the previous
          // session's ratings never survive a successful load.
          if (result?.messages) onMessagesLoaded(result.messages);
        })
        .catch(() => {
          // Error already handled in store
        });
    },
    [buildingId, chatStore, orgId, userIdentifier, onMessagesLoaded]
  );

  /** Prewarm, then hydrate — unless the thread already holds its messages. */
  const activateSession = useCallback(
    (session: SessionRef) => {
      void runSessionPrewarm(session);
      // Skip loading for brand-new empty sessions (WHA-2859)
      if (session.messageCount === 0) return;
      if (chatStore.threads.get(session.threadId)?.isHydrated) return;
      loadMessagesFor(session);
    },
    [chatStore, loadMessagesFor, runSessionPrewarm]
  );

  return { loadMessagesFor, activateSession };
}

/** Load the building's sessions, and (re)activate whichever becomes active. */
function useSessionBootstrap({
  buildingId,
  orgId,
  userIdentifier,
  sessionsLoading,
  activeSession,
  activateSession,
}: {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
  sessionsLoading: boolean;
  activeSession: SessionRef | null | undefined;
  activateSession: (session: SessionRef) => void;
}) {
  const chatStore = useChatStore();

  // Initialize thread when component mounts or dependencies change
  useEffect(() => {
    if (!orgId || !userIdentifier) return;
    chatStore.initThread({ buildingId, orgId, userId: userIdentifier });
  }, [buildingId, orgId, userIdentifier, chatStore]);

  // Load sessions when building changes (WHA-1465)
  useLayoutEffect(() => {
    if (!orgId) return;
    // Clear stale active session before loading fresh sessions for this building
    chatStore.clearBuildingChatState(buildingId);
    chatStore.loadSessions({ orgId, buildingId });
  }, [buildingId, orgId, chatStore]);

  // Auto-load messages when sessions finish loading (not when a new session is
  // created during send).
  const prevSessionsLoadingRef = useRef(sessionsLoading);
  useEffect(() => {
    const sessionsJustLoaded = prevSessionsLoadingRef.current && !sessionsLoading;
    prevSessionsLoadingRef.current = sessionsLoading;
    if (!sessionsJustLoaded || !orgId || !userIdentifier || !activeSession) return;
    activateSession(activeSession);
  }, [sessionsLoading, activeSession, orgId, userIdentifier, activateSession]);

  // React to the active session actually changing, e.g. after deletion (WHA-2336)
  const prevActiveSessionIdRef = useRef(activeSession?.id);
  useEffect(() => {
    const prevId = prevActiveSessionIdRef.current;
    prevActiveSessionIdRef.current = activeSession?.id;
    if (!prevId || prevId === activeSession?.id) return;
    if (!orgId || !userIdentifier || !activeSession) return;
    activateSession(activeSession);
  }, [activeSession, orgId, userIdentifier, activateSession]);
}

/** Types an auto-generated session title out one character at a time. */
function useSessionTitleAnimation() {
  const chatStore = useChatStore();
  const [animatingTitle, setAnimatingTitle] = useState<{
    sessionId: string;
    fullTitle: string;
    displayedChars: number;
  } | null>(null);

  useEffect(() => {
    if (!animatingTitle) return;
    const { sessionId, fullTitle, displayedChars } = animatingTitle;
    const timer = setTimeout(() => {
      const nextChars = displayedChars + 1;
      chatStore.setSessionTitle(sessionId, fullTitle.slice(0, nextChars));
      // The last character also ends the animation, so completion never needs
      // a state update from the effect body itself.
      setAnimatingTitle(
        nextChars >= fullTitle.length ? null : { ...animatingTitle, displayedChars: nextChars }
      );
    }, 30); // 30ms per character for smooth animation
    return () => clearTimeout(timer);
  }, [animatingTitle, chatStore]);

  return { animatingTitle, setAnimatingTitle };
}

/** The session-list callbacks the chat header drives. */
function useSessionActions({
  buildingId,
  orgId,
  userIdentifier,
  loadMessagesFor,
  runSessionPrewarm,
}: {
  buildingId: string;
  orgId?: string | null;
  userIdentifier: string | null;
  loadMessagesFor: (session: { id: string; threadId: string }) => void;
  runSessionPrewarm: (session: { id: string; messageCount: number }) => Promise<unknown>;
}) {
  const chatStore = useChatStore();

  const handleSessionSelect = useCallback(
    (sessionId: string) => {
      if (!orgId || !userIdentifier) return;
      chatStore.switchSession({ buildingId, sessionId });
      const session = chatStore.sessions.get(sessionId);
      if (!session) return;
      void runSessionPrewarm(session);
      loadMessagesFor(session);
    },
    [buildingId, orgId, userIdentifier, chatStore, loadMessagesFor, runSessionPrewarm]
  );

  const handleSessionCreate = useCallback(() => {
    if (!orgId) return;
    chatStore.createSession({ orgId, buildingId });
  }, [buildingId, orgId, chatStore]);

  const handleSessionArchive = useCallback(
    (sessionId: string) => {
      if (!orgId || !userIdentifier) return;
      chatStore.archiveSession({ sessionId, buildingId, orgId, userId: userIdentifier });
    },
    [buildingId, orgId, userIdentifier, chatStore]
  );

  const handleSessionRename = useCallback(
    async (sessionId: string, title: string) => {
      await chatStore.renameSession({ sessionId, title });
    },
    [chatStore]
  );

  return {
    handleSessionSelect,
    handleSessionCreate,
    handleSessionArchive,
    handleSessionRename,
  };
}

/** Roll the session back to its last good checkpoint and re-hydrate. */
function useChatRefresh({
  buildingId,
  orgId,
  threadId,
  activeSession,
  onMessagesLoaded,
  onMessagesReset,
}: {
  buildingId: string;
  orgId?: string | null;
  threadId: string | null;
  activeSession: { id: string; threadId: string } | null | undefined;
  onMessagesLoaded: (messages: LoadedMessage[]) => void;
  onMessagesReset: () => void;
}) {
  const chatStore = useChatStore();
  const [isRefreshingChat, setIsRefreshingChat] = useState(false);
  const canRefreshChat = Boolean(orgId && activeSession?.id && threadId);

  const handleRefreshChat = useCallback(async () => {
    if (isRefreshingChat || !orgId || !activeSession?.id || !activeSession.threadId) return;
    setIsRefreshingChat(true);
    try {
      const result = await chatStore.rollbackSession({
        sessionId: activeSession.id,
        threadId: activeSession.threadId,
        buildingId,
        orgId,
      });
      if (result?.messages?.messages?.length) {
        onMessagesLoaded(result.messages.messages);
      } else {
        // First-message failure: rollback cleared all messages, so clear the
        // feedback that belonged to them.
        onMessagesReset();
      }
    } catch {
      // Error is handled in store and reflected inline via chat messages.
    } finally {
      setIsRefreshingChat(false);
    }
  }, [
    activeSession,
    buildingId,
    chatStore,
    orgId,
    isRefreshingChat,
    onMessagesLoaded,
    onMessagesReset,
  ]);

  return { canRefreshChat, isRefreshingChat, handleRefreshChat };
}
